/**
 * useBankAbgleich — DB-Logik für den wöchentlichen Bank-CSV-Abgleich.
 *
 * Feature bank-csv-abgleich. Nutzt die bereits vorhandenen Tabellen
 * (import_transaktionen, finanz_konten, finanz_kategorien, finanz_transaktionen,
 * rechnungen, zahlungseingaenge). KEINE neue Migration nötig.
 *
 * GoBD / Sicherheit: Es werden ausschließlich VORSCHLÄGE erzeugt — jede
 * Buchung/Zahlung wird einzeln per 1-Klick bestätigt. Keine Massen-Auto-Buchung.
 * RLS/Policies bleiben unangetastet (Inhaber-only).
 */

import { useCallback, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Rechnung } from '@/lib/database.types';
import type { BankCsvZeile } from '@/lib/bankCsv';

// ============================================================
// Firmen-Konfiguration (eine Quelle der Wahrheit)
// ============================================================

export type FirmaKey = 'sv' | 'lcs' | 'privat' | 'immobilien';

export interface FirmaAbgleichConfig {
  key: FirmaKey;
  label: string;
  /** import_transaktionen.firma (siehe TransaktionKlaerungPage-Filter). */
  importFirma: string;
  /** finanz_transaktionen.firma. */
  finanzFirma: string;
  /** rechnungen.business_unit (offene Ausgangsrechnungen). */
  businessUnit: string;
  /** Standard-Zielkonto (finanz_konten.id) — Geschäftskonto der Firma. */
  defaultKontoId: string;
  /** finanz_konten.firma (für die Konto-Auswahl-Liste). */
  kontoFirma: string;
  /**
   * Erkennungs-Muster für die automatische Konto-Erkennung beim CSV-Upload.
   * Enthält Kontonummer und IBAN der Firma — wird im rohtext der geparsten
   * Zeilen gesucht (siehe erkenneFirmaAusZeilen).
   */
  erkennung: string[];
}

/**
 * Zentrale Firmen-Map. Die Strings entsprechen den real in der DB genutzten
 * Werten (laut Aufgabenstellung + bestehender TransaktionKlaerungPage):
 *  - import_transaktionen.firma: 'sv_buero_koop' | 'lcs' | 'privat'
 *  - finanz_konten.firma:        'sv_buero_koop' | 'lcs_gmbh' | 'privat'
 *  - rechnungen.business_unit:   'gutachten' | 'mietwagen'
 *
 * Privat & Immobilien/Ferienwohnung teilen sich die registrierte Buchhaltungs-
 * Firma 'privat' (nur sv_buero_koop, lcs, prolea_fzco, pv_gbr, privat sind als
 * Buchhaltungs-Firmen erlaubt — die DB blockiert andere Werte). Die fachliche
 * Trennung erfolgt über businessUnit ('privat' vs. 'immobilien').
 */
export const FIRMA_ABGLEICH: Record<FirmaKey, FirmaAbgleichConfig> = {
  sv: {
    key: 'sv',
    label: 'SV Büro',
    importFirma: 'sv_buero_koop',
    finanzFirma: 'sv_buero_koop',
    businessUnit: 'gutachten',
    defaultKontoId: 'ca77106a-43a6-4fd0-ad7a-3ce09f0ad48a',
    kontoFirma: 'sv_buero_koop',
    erkennung: ['1318195', 'DE11450500010001318195'],
  },
  lcs: {
    key: 'lcs',
    label: 'LCS',
    importFirma: 'lcs',
    finanzFirma: 'lcs_gmbh',
    businessUnit: 'mietwagen',
    defaultKontoId: '55f07453-2960-4373-844b-981c6624040d',
    kontoFirma: 'lcs_gmbh',
    erkennung: ['425108', 'DE33450500010000425108'],
  },
  privat: {
    key: 'privat',
    label: 'Privat',
    importFirma: 'privat',
    finanzFirma: 'privat',
    businessUnit: 'privat',
    defaultKontoId: '',
    kontoFirma: 'privat',
    erkennung: ['8813339', 'DE89450500010008813339'],
  },
  immobilien: {
    key: 'immobilien',
    label: 'Immobilien',
    importFirma: 'privat',
    finanzFirma: 'privat',
    businessUnit: 'immobilien',
    defaultKontoId: '',
    kontoFirma: 'privat',
    erkennung: ['1476738', 'DE57450500010001476738'],
  },
};

export function getFirmaConfig(key: FirmaKey): FirmaAbgleichConfig {
  return FIRMA_ABGLEICH[key] || FIRMA_ABGLEICH.sv;
}

/**
 * Automatische Konto-Erkennung beim Upload: Durchsucht die `rohtext`-Felder der
 * ersten ~5 geparsten Zeilen nach den in FIRMA_ABGLEICH hinterlegten Mustern
 * (Kontonummer / IBAN) und liefert den FirmaKey der erkannten Firma — oder
 * `null`, wenn kein Muster gefunden wurde. Wirft NICHT.
 */
export function erkenneFirmaAusZeilen(zeilen: BankCsvZeile[]): FirmaKey | null {
  try {
    if (!Array.isArray(zeilen) || zeilen.length === 0) return null;
    // Rohtext der ersten ~5 Zeilen zusammenführen (Großschreibung für IBAN-Match).
    const heuristik = zeilen
      .slice(0, 5)
      .map((z) => (z?.rohtext || '').toUpperCase())
      .join(' \n ');
    if (!heuristik.trim()) return null;

    for (const cfg of Object.values(FIRMA_ABGLEICH)) {
      for (const muster of cfg.erkennung) {
        const m = (muster || '').toUpperCase().trim();
        if (m && heuristik.includes(m)) {
          return cfg.key;
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================
// Typen
// ============================================================

export interface ImportTransaktionRow {
  id: string;
  firma: string;
  buchung_datum: string;
  wert_datum: string | null;
  betrag: number;
  waehrung: string;
  beschreibung: string | null;
  rohtext: string | null;
  status: string;
  kategorie: string | null;
  ist_einnahme: boolean | null;
  ist_geschaeftlich: boolean | null;
  zugeordnet_konto_id: string | null;
  created_at: string;
}

export interface FinanzKonto {
  id: string;
  name: string;
  firma: string;
  typ: string | null;
}

export interface FinanzKategorie {
  id: string;
  name: string;
  typ: 'einnahme' | 'ausgabe' | string;
  parent_id: string | null;
}

export interface ImportErgebnis {
  neu: number;
  duplikate: number;
  fehler: number;
}

/** Vorschlag für eine passende offene Rechnung zu einem Zahlungseingang. */
export interface RechnungsMatch {
  rechnung: Rechnung;
  /**
   * 'ak'       = AK-Nummer aus dem Text trifft die Rechnungsnummer,
   * 'betrag'   = exakter Betrags-Match,
   * 'referenz' = Name/Nr im Verwendungszweck.
   */
  grund: 'ak' | 'betrag' | 'referenz';
}

function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

/**
 * Extrahiert AK-Aktennummern (z. B. "AK123456") aus beliebigem Text.
 * Erlaubt 6–8 Ziffern. Gibt normalisierte (Großbuchstaben-)Tokens zurück,
 * dedupliziert. Wirft NICHT.
 */
function extrahiereAkNummern(...texte: (string | null | undefined)[]): string[] {
  const treffer = new Set<string>();
  try {
    for (const t of texte) {
      if (!t) continue;
      const re = /AK\d{6,8}/gi;
      let m: RegExpExecArray | null;
      while ((m = re.exec(t)) !== null) {
        treffer.add(m[0].toUpperCase());
      }
    }
  } catch {
    // defensiv: bei unerwartetem Fehler einfach leeres Ergebnis liefern
  }
  return Array.from(treffer);
}

// ============================================================
// Hook
// ============================================================

export function useBankAbgleich(firmaKey: FirmaKey) {
  const cfg = getFirmaConfig(firmaKey);
  const [busy, setBusy] = useState(false);

  // ── Konten der Firma laden (für Zielkonto-Auswahl) ──
  const ladeKonten = useCallback(async (): Promise<FinanzKonto[]> => {
    try {
      const { data, error } = await supabase
        .from('finanz_konten')
        .select('id, name, firma, typ')
        .eq('firma', cfg.kontoFirma)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as FinanzKonto[]) || [];
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Konten laden fehlgeschlagen:', err);
      return [];
    }
  }, [cfg.kontoFirma]);

  // ── Kategorien laden (für Buchungsvorschlag) ──
  const ladeKategorien = useCallback(async (): Promise<FinanzKategorie[]> => {
    try {
      const { data, error } = await supabase
        .from('finanz_kategorien')
        .select('id, name, typ, parent_id')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as FinanzKategorie[]) || [];
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Kategorien laden fehlgeschlagen:', err);
      return [];
    }
  }, []);

  // ── Transaktionen der Firma laden (optional Monat/Status-Filter) ──
  const ladeTransaktionen = useCallback(async (opts?: {
    monat?: string;       // 'YYYY-MM'
    status?: string;      // 'unklar' | 'geklaert' | ...
  }): Promise<ImportTransaktionRow[]> => {
    try {
      let query = supabase
        .from('import_transaktionen')
        .select('*')
        .eq('firma', cfg.importFirma)
        .order('buchung_datum', { ascending: false })
        .limit(500);

      if (opts?.status) query = query.eq('status', opts.status);
      if (opts?.monat && /^\d{4}-\d{2}$/.test(opts.monat)) {
        const von = `${opts.monat}-01`;
        // letzter Tag des Monats
        const [y, m] = opts.monat.split('-').map(Number);
        const bis = `${opts.monat}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
        query = query.gte('buchung_datum', von).lte('buchung_datum', bis);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as ImportTransaktionRow[]) || [];
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Transaktionen laden fehlgeschlagen:', err);
      return [];
    }
  }, [cfg.importFirma]);

  // ── Jüngstes Import-/Buchungsdatum (für Erinnerungs-Banner) ──
  const letztesUploadDatum = useCallback(async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('import_transaktionen')
        .select('buchung_datum, created_at')
        .eq('firma', cfg.importFirma)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = (data as { buchung_datum: string; created_at: string }[] | null)?.[0];
      return row?.created_at || row?.buchung_datum || null;
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Letztes Upload-Datum fehlgeschlagen:', err);
      return null;
    }
  }, [cfg.importFirma]);

  // ── CSV-Zeilen importieren (mit Dedupe) ──
  const importiere = useCallback(async (
    zeilen: BankCsvZeile[],
    zielKontoId: string,
  ): Promise<ImportErgebnis> => {
    setBusy(true);
    const ergebnis: ImportErgebnis = { neu: 0, duplikate: 0, fehler: 0 };
    try {
      const { data: vorhanden, error: ladeErr } = await supabase
        .from('import_transaktionen')
        .select('buchung_datum, betrag, beschreibung')
        .eq('firma', cfg.importFirma)
        .limit(5000);
      if (ladeErr && !isTableNotFound(ladeErr)) throw ladeErr;

      const dedupeKey = (d: string, b: number, beschr: string | null) =>
        `${d}|${Number(b).toFixed(2)}|${(beschr || '').trim()}`;

      const existierend = new Set<string>(
        (vorhanden || []).map((v: any) =>
          dedupeKey(v.buchung_datum, v.betrag, v.beschreibung),
        ),
      );

      const einzufuegen: Record<string, unknown>[] = [];
      const gesehen = new Set<string>(existierend);

      for (const z of zeilen) {
        const key = dedupeKey(z.buchungDatum, z.betrag, z.beschreibung);
        if (gesehen.has(key)) {
          ergebnis.duplikate++;
          continue;
        }
        gesehen.add(key);
        einzufuegen.push({
          firma: cfg.importFirma,
          buchung_datum: z.buchungDatum,
          wert_datum: z.wertDatum,
          betrag: z.betrag,
          waehrung: z.waehrung,
          beschreibung: z.beschreibung,
          rohtext: z.rohtext,
          ist_einnahme: z.betrag > 0,
          zugeordnet_konto_id: zielKontoId || null,
          status: 'unklar',
        });
      }

      if (einzufuegen.length > 0) {
        const BATCH = 200;
        for (let i = 0; i < einzufuegen.length; i += BATCH) {
          const batch = einzufuegen.slice(i, i + BATCH);
          const { error } = await supabase
            .from('import_transaktionen')
            .insert(batch as any);
          if (error) {
            for (const row of batch) {
              const { error: e2 } = await supabase
                .from('import_transaktionen')
                .insert(row as any);
              if (e2) {
                console.error('Zeile fehlgeschlagen:', e2, row);
                ergebnis.fehler++;
              } else {
                ergebnis.neu++;
              }
            }
          } else {
            ergebnis.neu += batch.length;
          }
        }
      }

      return ergebnis;
    } catch (err) {
      console.error('Import fehlgeschlagen:', err);
      ergebnis.fehler += zeilen.length - ergebnis.neu - ergebnis.duplikate;
      return ergebnis;
    } finally {
      setBusy(false);
    }
  }, [cfg.importFirma]);

  // ── Passende offene Rechnungen zu einem Eingang vorschlagen ──
  const findeRechnungsMatches = useCallback(async (
    tx: ImportTransaktionRow,
  ): Promise<RechnungsMatch[]> => {
    if (tx.betrag <= 0) return [];
    try {
      const { data, error } = await supabase
        .from('rechnungen')
        .select('*')
        .eq('business_unit', cfg.businessUnit)
        .neq('status', 'bezahlt')
        .order('rechnungsdatum', { ascending: false })
        .limit(300);
      if (error) throw error;

      const rechnungen = (data as Rechnung[]) || [];
      const matches: RechnungsMatch[] = [];
      const erfasst = new Set<string>();
      const beschr = (tx.beschreibung || '').toLowerCase();

      const akNummern = extrahiereAkNummern(tx.beschreibung, tx.rohtext);
      if (akNummern.length > 0) {
        for (const r of rechnungen) {
          if (erfasst.has(r.id)) continue;
          const nrUpper = (r.rechnungs_nr || '').toUpperCase();
          if (!nrUpper) continue;
          if (akNummern.some((ak) => nrUpper.includes(ak))) {
            matches.push({ rechnung: r, grund: 'ak' });
            erfasst.add(r.id);
          }
        }
      }

      for (const r of rechnungen) {
        if (erfasst.has(r.id)) continue;
        const brutto = Number(r.brutto_betrag) || 0;
        if (Math.abs(brutto - tx.betrag) <= 0.01) {
          matches.push({ rechnung: r, grund: 'betrag' });
          erfasst.add(r.id);
          continue;
        }
        const nr = (r.rechnungs_nr || '').toLowerCase().trim();
        const name = (r.empfaenger_name || '').toLowerCase().trim();
        if ((nr && beschr.includes(nr)) || (name && name.length >= 4 && beschr.includes(name))) {
          matches.push({ rechnung: r, grund: 'referenz' });
          erfasst.add(r.id);
        }
      }

      const rang: Record<RechnungsMatch['grund'], number> = { ak: 0, betrag: 1, referenz: 2 };
      matches.sort((a, b) => rang[a.grund] - rang[b.grund]);
      return matches.slice(0, 5);
    } catch (err) {
      if (!isTableNotFound(err)) console.error('Rechnungs-Match fehlgeschlagen:', err);
      return [];
    }
  }, [cfg.businessUnit]);

  // ── Zahlung buchen: zahlungseingaenge + rechnung bezahlt + tx geklaert ──
  const alsZahlungBuchen = useCallback(async (
    tx: ImportTransaktionRow,
    rechnung: Rechnung,
  ): Promise<boolean> => {
    setBusy(true);
    try {
      const { error: zeErr } = await supabase
        .from('zahlungseingaenge')
        .insert({
          rechnung_id: rechnung.id,
          betrag: tx.betrag,
          datum: tx.buchung_datum,
          zahlungsart: 'ueberweisung',
          absender: tx.beschreibung?.slice(0, 200) || null,
          verwendungszweck: tx.beschreibung || null,
          status: 'zugeordnet',
        } as any);
      if (zeErr) throw zeErr;

      const { error: rErr } = await supabase
        .from('rechnungen')
        .update({
          status: 'bezahlt',
          bezahlt_am: tx.buchung_datum,
          bezahlt_betrag: tx.betrag,
        } as any)
        .eq('id', rechnung.id);
      if (rErr) throw rErr;

      const { error: txErr } = await supabase
        .from('import_transaktionen')
        .update({ status: 'geklaert' } as any)
        .eq('id', tx.id);
      if (txErr) throw txErr;

      return true;
    } catch (err) {
      console.error('Als Zahlung buchen fehlgeschlagen:', err);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  // ── Buchung erstellen: finanz_transaktionen + tx geklaert ──
  const buchen = useCallback(async (
    tx: ImportTransaktionRow,
    kategorieId: string | null,
  ): Promise<boolean> => {
    setBusy(true);
    try {
      const istEinnahme = tx.betrag > 0;
      const { error: ftErr } = await supabase
        .from('finanz_transaktionen')
        .insert({
          konto_id: tx.zugeordnet_konto_id || cfg.defaultKontoId,
          kategorie_id: kategorieId,
          datum: tx.buchung_datum,
          betrag: tx.betrag,
          beschreibung: tx.beschreibung || null,
          verwendungszweck: tx.beschreibung || null,
          typ: istEinnahme ? 'einnahme' : 'ausgabe',
          ist_abgeglichen: true,
          firma: cfg.finanzFirma,
        } as any);
      if (ftErr) throw ftErr;

      const { error: txErr } = await supabase
        .from('import_transaktionen')
        .update({ status: 'geklaert' } as any)
        .eq('id', tx.id);
      if (txErr) throw txErr;

      return true;
    } catch (err) {
      console.error('Buchen fehlgeschlagen:', err);
      return false;
    } finally {
      setBusy(false);
    }
  }, [cfg.defaultKontoId, cfg.finanzFirma]);

  return useMemo(() => ({
    cfg,
    busy,
    ladeKonten,
    ladeKategorien,
    ladeTransaktionen,
    letztesUploadDatum,
    importiere,
    findeRechnungsMatches,
    alsZahlungBuchen,
    buchen,
  }), [
    cfg,
    busy,
    ladeKonten,
    ladeKategorien,
    ladeTransaktionen,
    letztesUploadDatum,
    importiere,
    findeRechnungsMatches,
    alsZahlungBuchen,
    buchen,
  ]);
}

// ============================================================
// Buchungsvorschlag — Keyword-Regeln auf beschreibung
// ============================================================

export function schlageKategorieNameVor(
  beschreibung: string | null,
  istEinnahme: boolean,
): string {
  const b = (beschreibung || '').toLowerCase();

  const regeln: { keys: string[]; name: string }[] = [
    { keys: ['aok', 'knappschaft', 'krankenkasse', 'techniker krankenkasse', 'tk '], name: 'Sozialversicherung' },
    { keys: ['finanzamt', 'steuer', 'umsatzsteuer', 'vorauszahlung'], name: 'Steuern' },
    { keys: ['telekom', 'vodafone', 'o2', '1&1', 'telefon', 'internet'], name: 'Telekommunikation' },
    { keys: ['tankstelle', 'aral', 'shell', 'esso', 'bft', 'jet ', 'star '], name: 'Tankstelle' },
    { keys: ['miete', 'pacht'], name: 'Miete' },
    { keys: ['gehalt', 'lohn', 'lohn/gehalt'], name: 'Gehalt' },
    { keys: ['stripe', 'paypal', 'software', 'adobe', 'microsoft', 'google'], name: 'Software' },
    { keys: ['versicherung', 'allianz', 'huk', 'axa'], name: 'Versicherung' },
    { keys: ['amazon', 'ebay', 'büro', 'buero', 'staples'], name: 'Bürobedarf' },
    { keys: ['leasing', 'kredit', 'darlehen'], name: 'Leasing / Kredit' },
    { keys: ['bank', 'gebühr', 'gebuehr', 'entgelt'], name: 'Bankgebühren' },
  ];

  for (const r of regeln) {
    if (r.keys.some((k) => b.includes(k))) return r.name;
  }

  return istEinnahme ? 'Einnahmen' : 'Sonstige Ausgaben';
}
